import React from 'react';
import { colors, fonts, layout, buttons, messages } from '../styles/common';
import { ROUTES } from '../config/routes';
import { errorLogger, describeError } from '../utils/errorLogger';
import { isTranslationDomError, reloadWithTranslationDisabled } from '../utils/translationRecovery';

/**
 * アプリ全体の最後の受け皿となるエラーバウンダリ
 *
 * ルート単位のエラーは RouteErrorBoundary（react-router の errorElement）が扱うが、
 * そのエラー画面自身が落ちた場合や、RouterProvider より外側で例外が発生した場合は
 * どこにも捕まらず白画面になってしまう。ここはその最後の砦。
 *
 * ルーターやコンテキストに一切依存させないこと（依存すると、
 * それらが壊れている状況でこのバウンダリも一緒に落ちる）
 */

const styles = {
  page: {
    ...layout.fullScreenCenter,
    minHeight: '100vh'
  },
  container: {
    ...layout.container,
    textAlign: 'center'
  },
  icon: {
    fontSize: '48px',
    lineHeight: '1',
    marginBottom: '15px'
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors.primary,
    margin: '0 0 15px 0'
  },
  message: {
    fontSize: '15px',
    color: '#333',
    lineHeight: '1.8',
    margin: '0 0 20px 0'
  },
  hintBox: {
    ...messages.info,
    borderRadius: '4px',
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '1.8',
    textAlign: 'left',
    margin: '0 0 20px 0'
  },
  buttonArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  primaryButton: {
    ...buttons.base,
    ...buttons.primary
  },
  linkButton: {
    ...buttons.base,
    ...buttons.link
  },
  details: {
    marginTop: '20px',
    textAlign: 'left',
    fontSize: '12px',
    color: colors.textGray
  },
  pre: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    backgroundColor: colors.lightGray,
    border: `1px solid ${colors.borderGray}`,
    borderRadius: '4px',
    padding: '10px',
    fontFamily: 'monospace',
    maxHeight: '200px',
    overflowY: 'auto'
  }
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleReload = this.handleReload.bind(this);
    this.handleBackToTop = this.handleBackToTop.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // 記録自体が落ちてもエラー画面は出し続ける
    try {
      const context = isTranslationDomError(error)
        ? '[APP ERROR/TRANSLATION]'
        : '[APP ERROR]';
      if (error instanceof Error) {
        errorLogger.logError(context, error);
      } else {
        errorLogger.log(context, describeError(error));
      }
    } catch (e) {
      console.error('[APP ERROR] Failed to record error:', e, info);
    }
  }

  handleReload() {
    if (isTranslationDomError(this.state.error)) {
      // そのまま再読み込みすると再度翻訳されて同じ場所で落ちるため、翻訳を止めて読み込む
      reloadWithTranslationDisabled();
      return;
    }
    window.location.reload();
  }

  handleBackToTop() {
    window.location.href = ROUTES.ROOT;
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isTranslationError = isTranslationDomError(error);

    return (
      <div style={styles.page}>
        <div style={styles.container} role="alert">
          <div style={styles.icon} aria-hidden="true">⚠️</div>
          <h1 style={styles.title}>エラーが発生しました</h1>
          <p style={styles.message}>
            ご迷惑をおかけしております。お手数ですが、ページを再読み込みしてお試しください。
            入力途中の内容は保存されていない場合があります。
          </p>

          {isTranslationError && (
            <div style={styles.hintBox}>
              <strong>ブラウザの翻訳機能が原因の可能性があります</strong>
              <br />
              <span>下の「翻訳をオフにして再読み込み」を押すと、翻訳を止めた状態で開き直します。</span>
            </div>
          )}

          <div style={styles.buttonArea}>
            <button type="button" style={styles.primaryButton} onClick={this.handleReload}>
              {isTranslationError ? '翻訳をオフにして再読み込み' : 'ページを再読み込み'}
            </button>
            <button type="button" style={styles.linkButton} onClick={this.handleBackToTop}>
              最初の画面に戻る
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div style={styles.details}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px', fontFamily: fonts.main }}>
                開発用エラー詳細:
              </div>
              <pre style={styles.pre} translate="no">
                {describeError(error)}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
